import React, { useMemo, useState } from 'react';
import {
  localIsoDate,
  type DocumentElement as DocumentElementType,
} from '@eaco/forms-core';
import { FieldHeader, FieldHelpText, CalendarIcon } from './FieldParts';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';
import FieldPopover from './datetime/FieldPopover';
import CalendarPanel from './datetime/CalendarPanel';
import { todayISO } from './datetime/dateutils';

/** dd / mm / yyyy — the trigger format from the design. */
const formatSlash = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]} / ${m[2]} / ${m[1]}` : iso;
};

interface Props {
  element: DocumentElementType;
}

type EarliestType = 'any' | 'today_or_later' | 'specific';
type LatestType = 'any' | 'today_or_earlier' | 'specific';

const todayIso = (): string => localIsoDate();

/**
 * Render a yyyy-mm-dd string as a human-readable Australian-English
 * date (e.g. "5 March 2026"). Falls back to the raw string if it
 * doesn't parse, so a legacy value is never swallowed.
 */
const formatDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const DatePicker: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [touched, setTouched] = useState(false);

  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    earliestType?: EarliestType;
    earliestSpecificDate?: string;
    latestType?: LatestType;
    latestSpecificDate?: string;
    defaultValue?: string;
  };
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue);
  const label = data?.label || 'Date';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = data?.required || false;
  const earliestType: EarliestType = data?.earliestType || 'any';
  const latestType: LatestType = data?.latestType || 'any';

  // Effective configured bounds. Same shape as DateRange so the two
  // elements behave consistently.
  const cfgEarliest = useMemo<string | undefined>(() => {
    if (earliestType === 'specific')
      return data?.earliestSpecificDate || undefined;
    if (earliestType === 'today_or_later') return todayIso();
    return undefined;
  }, [earliestType, data?.earliestSpecificDate]);

  const cfgLatest = useMemo<string | undefined>(() => {
    if (latestType === 'specific') return data?.latestSpecificDate || undefined;
    if (latestType === 'today_or_earlier') return todayIso();
    return undefined;
  }, [latestType, data?.latestSpecificDate]);

  const handleChange = (next: string) => {
    setTouched(true);
    setValue(next);
  };

  // Out-of-bounds warning, fill-time only.
  const error = useMemo<string | null>(() => {
    if (isEditor || !touched || !value) return null;
    if (cfgEarliest && value < cfgEarliest) {
      return 'Date is outside the allowed range';
    }
    if (cfgLatest && value > cfgLatest) {
      return 'Date is outside the allowed range';
    }
    return null;
  }, [isEditor, touched, value, cfgEarliest, cfgLatest]);

  // Read-only (e.g. a completed result): render the chosen date as
  // static text — no date input, no calendar picker.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {displayValue ? (
            formatDate(displayValue)
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <FieldPopover
        id={element.id}
        icon={<CalendarIcon />}
        displayText={displayValue ? formatSlash(displayValue) : ''}
        placeholder="dd / mm / yyyy"
      >
        {(close) => (
          <CalendarPanel
            mode="single"
            selected={displayValue}
            min={cfgEarliest}
            max={cfgLatest}
            onSelect={(iso) => {
              handleChange(iso);
              close();
            }}
            footer={
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  className="text-[12.5px] font-bold"
                  style={{ color: '#2f6fdb' }}
                  onClick={() => {
                    const t = todayISO();
                    if (
                      (!cfgEarliest || t >= cfgEarliest) &&
                      (!cfgLatest || t <= cfgLatest)
                    ) {
                      handleChange(t);
                      close();
                    }
                  }}
                >
                  Today
                </button>
                <button
                  type="button"
                  className="text-[12.5px] font-semibold text-[#8a93a3]"
                  onClick={() => {
                    handleChange('');
                    close();
                  }}
                >
                  Clear
                </button>
              </div>
            }
          />
        )}
      </FieldPopover>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default DatePicker;
